/**
 * @file PoSupplierRefModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description "Add Supplier Ref & Confirm" (Cam, 2 Nov 2026 — Search
 *              Purchase Orders' row menu and right-click, "a modal like Add
 *              Supplied Qty"): the supplier's reference for a PO, and its
 *              status set to Confirmed in the same step — what the PO editor
 *              asks ("Mark as confirmed?") when a reference is added.
 *
 *   The PO and its lines (in their own unit, with tonnes) are shown so the
 *   supplier's acknowledgement can be checked against them; the reference is
 *   typed (Enter saves); "Set Confirmed" is on by default and greyed with the
 *   reason when it can't be (billed, or after delivery a move that isn't
 *   forward). Nothing is emailed to the supplier. Rules: utils/poSupplierRef
 *   (the server's copy is generated from it); server
 *   services/poSupplierRefService (GET / PATCH /api/purchase-orders/db/:id/
 *   supplier-ref). An invoiced order quotes the reference: a changed one
 *   offers the invoice the change (InvoiceEditOfferDialog), never applied by
 *   itself.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import QtyWithTonnes from './QtyWithTonnes';
import InvoiceEditOfferDialog from './InvoiceEditOfferDialog';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort } from '../utils/dateFormatters';
import { statusMoveLabel } from '../utils/poStatusMove';
import { hasInvoiceOffer } from '../utils/afterDelivery';
import {
  SUPPLIER_REF_ACTION, SUPPLIER_REF_MAX, cleanSupplierRef, planSupplierRef
} from '../utils/poSupplierRef';
import './AddSuppliedQuantityModal.css';
import './PoSupplierRefModal.css';

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {number} props.poDbId — the PO's local id
 * @param {() => void} props.onClose — closed without saving
 * @param {(result: { message: string, status?: string }) => void} props.onSaved — saved (the table refreshes)
 */
function PoSupplierRefModal({ isOpen, poDbId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reference, setReference] = useState('');
  const [confirm, setConfirm] = useState(true);
  const [invoiceOffer, setInvoiceOffer] = useState(null);

  useSlideOutButton([loading, saving, confirm, data?.po?.status]);

  useEffect(() => {
    if (!isOpen || !poDbId) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    setData(null);
    setInvoiceOffer(null);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/purchase-orders/db/${encodeURIComponent(poDbId)}/supplier-ref`, { credentials: 'include' });
        const body = await res.json().catch(() => ({}));
        if (!live) return;
        if (!res.ok) throw new Error(body.error || `Could not load the purchase order (${res.status})`);
        setData(body);
        setReference(body.po?.supplierReference || '');
        setConfirm(!body.confirm?.block);
      } catch (e) {
        if (live) setError(e.message);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [isOpen, poDbId]);

  // The reference box takes the cursor once the PO is shown.
  useEffect(() => {
    if (!loading && data && !data.block) {
      const t = setTimeout(() => document.getElementById('po-supplier-ref-input')?.focus(), 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [loading, data]);

  const po = data?.po;
  const confirmInfo = data?.confirm || {};
  const blocked = !!data?.block;
  const delivered = (data?.delivered || []).length > 0;
  const plan = po ? planSupplierRef({
    stored: { supplierReference: po.supplierReference, status: po.status },
    asked: { supplierReference: reference, confirm: confirm && !confirmInfo.block },
    delivered
  }) : null;
  const saveBlock = !po ? 'Loading…'
    : blocked ? data.block
      : plan?.error ? plan.error
        : plan?.unchanged ? (confirmInfo.already ? 'It already has this reference and is Confirmed' : 'Nothing has changed')
          : null;
  // What Save does, from the switch (the plan has nothing to say until a reference is typed).
  const willConfirm = !!po && confirm && !confirmInfo.block && !confirmInfo.already;
  const saveLabel = saving ? 'Saving…' : willConfirm ? 'Save & Confirm' : 'Save reference';

  const handleSave = async () => {
    if (saving || saveBlock) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/db/${encodeURIComponent(poDbId)}/supplier-ref`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_reference: cleanSupplierRef(reference),
          confirm: !!plan?.moving,
          expected_supplier_reference: po.supplierReference || '',
          expected_status: po.status
        })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Could not save (${res.status})`);
      if (hasInvoiceOffer(body.invoice_offer) && body.invoice_offer_order_id) {
        setInvoiceOffer({ offer: body.invoice_offer, orderId: body.invoice_offer_order_id, result: body });
        return;
      }
      onSaved?.(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const statusNote = () => {
    if (!po) return null;
    const now = statusMoveLabel(po.status);
    if (confirmInfo.already) return `It is Confirmed already — saving changes only the supplier's reference.`;
    if (confirmInfo.block) return `${confirmInfo.block}`;
    if (!confirm) return `The status stays ${now}.`;
    return `${now} → Confirmed${po.sentToContact ? '' : ', marked as sent to the supplier'}. Nothing is emailed to anyone${delivered ? ' — its order is delivered, so this is a record' : ''}.`;
  };

  const footer = (
    <div className="po-supplier-ref-footer">
      <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
        <div className="icon-wrapper">
          <span className="material-symbols-rounded">close</span>
        </div>
        <span className="btn-label">Cancel</span>
      </button>
      <button
        className="btn-slideout btn-slideout-primary"
        data-write-action
        onClick={handleSave}
        disabled={saving || !!saveBlock}
        title={saveBlock || undefined}
      >
        <div className="icon-wrapper">
          <span className="material-symbols-rounded">{saving ? 'progress_activity' : willConfirm ? 'task_alt' : 'save'}</span>
        </div>
        <span className="btn-label">{saveLabel}</span>
      </button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={SUPPLIER_REF_ACTION}
        size="lg"
        footer={!loading && po ? footer : null}
        className="add-supplied-qty-modal po-supplier-ref-modal"
      >
        {loading ? (
          <div className="loading-state"><LoadingSpinner /></div>
        ) : !po ? (
          <div className="supplied-qty-content">
            <div className="error-message">{error || 'Could not load the purchase order.'}</div>
          </div>
        ) : (
          <div className="supplied-qty-content">
            {error && <div className="error-message">{error}</div>}
            {blocked && <div className="error-message">{data.block}.</div>}

            <div className="delivery-status-row po-supplier-ref-row">
              <div className="delivery-status-field po-supplier-ref-field">
                <label htmlFor="po-supplier-ref-input">Supplier's reference</label>
                <Input
                  id="po-supplier-ref-input"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  onEnter={handleSave}
                  placeholder="Their order / acknowledgement number"
                  maxLength={SUPPLIER_REF_MAX}
                  disabled={blocked || saving}
                  autoComplete="off"
                  data-write-action
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className={`delivery-status-field po-supplier-ref-confirm${confirmInfo.block || confirmInfo.already ? ' is-disabled' : ''}`}
                   title={confirmInfo.block || (confirmInfo.already ? 'It is Confirmed already' : undefined)}>
                <label htmlFor="po-supplier-ref-confirm">Set Confirmed</label>
                <div className="checkbox-container">
                  <div className="check light">
                    <input
                      id="po-supplier-ref-confirm"
                      type="checkbox"
                      checked={confirmInfo.already ? true : (confirm && !confirmInfo.block)}
                      onChange={(e) => setConfirm(e.target.checked)}
                      disabled={blocked || saving || !!confirmInfo.block || !!confirmInfo.already}
                      data-write-action
                    />
                    <label htmlFor="po-supplier-ref-confirm"></label>
                  </div>
                </div>
              </div>
              <p className="delivery-status-note">{statusNote()}</p>
            </div>

            <div className="order-info po-supplier-ref-info">
              <span className="order-number">{po.number}</span>
              <span className="customer-name">{po.supplier || 'Supplier'}</span>
              <span className="po-supplier-ref-meta">
                <span className="po-supplier-ref-status">{statusMoveLabel(po.status)}</span>
                {po.deliveryDate && <span>Delivery {formatDateShort(po.deliveryDate)}</span>}
                {(data.orders || []).length > 0 && (
                  <span>For {data.orders.map(o => o.number).join(', ')}{po.customer ? ` · ${po.customer}` : ''}</span>
                )}
                {!(data.orders || []).length && <span>{po.customer ? `For ${po.customer}` : 'For our stock'}</span>}
              </span>
            </div>

            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-right">Ordered Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lines || []).length === 0 && (
                    <tr><td colSpan={2} className="po-supplier-ref-empty">No lines on this purchase order.</td></tr>
                  )}
                  {(data.lines || []).map((line, i) => (
                    <tr key={`${line.itemCode || 'line'}-${i}`}>
                      <td>
                        {line.itemCode && <span className="product-code">{line.itemCode}</span>}
                        {line.description}
                      </td>
                      <td className="text-right"><QtyWithTonnes quantity={line.quantity} line={line} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {invoiceOffer && (
        <InvoiceEditOfferDialog
          offer={invoiceOffer.offer}
          orderId={invoiceOffer.orderId}
          reason={`Supplier reference added on ${po?.number || 'the PO'}`}
          onDone={(text) => {
            const result = invoiceOffer.result;
            setInvoiceOffer(null);
            onSaved?.({ ...result, message: [result.message, text].filter(Boolean).join(' ') });
          }}
        />
      )}
    </>
  );
}

export default PoSupplierRefModal;
